import React, { useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { useReactToPrint } from "react-to-print";
import config from "../config/Config";
import { formatDate, getAppliedJobDetails } from "../helper/My_Helper";

const EPFForm2 = ({ candidateData = {} }) => {
  const formRef = useRef();
  const appliedJob = getAppliedJobDetails(candidateData);

  const doj= candidateData?.annexure_eleven_form_data?.candidate_doj || appliedJob?.onboard_date ;
  // Extract data from the API response with safe fallbacks
  const extractedData = useMemo(() => {
    const applicantForm = candidateData?.applicant_form_data || {};
    const annexureEleven = candidateData?.annexure_eleven_form_data || {};
    const fullName = applicantForm.full_name || {};
    
    return {
      // Personal Details
      firstName: fullName.first_name || "",
      middleName: fullName.middle_name || "",
      surname: fullName.surname || "",
      fullName: [fullName.first_name, fullName.middle_name, fullName.surname]
        .filter(Boolean)
        .join(" ") || candidateData?.name || "",
      
      // Date of Birth - using the format from your data: "dob1 || dob2"
      dob: applicantForm.dob || {},
      dobDate: applicantForm.dob?.date || "",
      dobMonth: applicantForm.dob?.month || "",
      dobYear: applicantForm.dob?.year || "",
      dobFormatted: applicantForm.dob 
        ? `${applicantForm.dob.date || ""} ${applicantForm.dob.month || ""} ${applicantForm.dob.year || ""}`.trim() 
        : "______________",
      
      // Father/Husband Name
      fatherHusbandName: applicantForm.father_hushband_name || "",
      
      // Gender & Marital Status
      gender: applicantForm.gender || "______",
      maritalStatus: applicantForm.marital_status || "______",
      
      // Address - using communication address as primary
      address: applicantForm.communication_address?.address || "",
      
      // Bank Details (from annexure_eleven_form_data)
      bankName: annexureEleven.bank_details?.bank_name || "",
      branchIfsc: annexureEleven.bank_details?.branch_ifsc || "",
      accountNo: annexureEleven.bank_details?.account_no || "",
      
      // Emergency Contacts
      emergencyLocal: annexureEleven.emergency_contact_local || {},
      emergencyPermanent: annexureEleven.emergency_contact_permanent || {},
      
      // Other fields
      panNumber: annexureEleven.pan_number || "",
      bloodGroup: annexureEleven.blood_group || "",
      employeeNumber: annexureEleven.employee_number || "",
      designation: annexureEleven.designation || "",
      
      // Form statuses
      applicantFormStatus: candidateData?.applicant_form_status || "Pending",
      annexureFormStatus: candidateData?.annexure_eleven_form_status || "Pending",
      
      // Direct fields from root
      email: candidateData?.email || "",
      mobileNo: candidateData?.mobile_no || "",
      photo: candidateData?.photo || "",
      jobTitle: candidateData?.job_title || "",
      projectName: candidateData?.project_name || "",
      department: candidateData?.department || "",
      totalExperience: candidateData?.total_experience || "",
      currentCTC: candidateData?.current_ctc || "",
      formStatus: candidateData?.form_status || "",
      profileStatus: candidateData?.profile_status || "",
      kycSteps: candidateData?.kyc_steps || "",
      addDate: candidateData?.add_date || "",
      updatedOn: candidateData?.updated_on || "",
      appliedFrom: candidateData?.applied_from || "",
      
      // EPF Nominees from JSON
      epfNominees: appliedJob?.epf_nominees || [],
      
      // EPS Family Members from JSON
      epsFamilyMembers: appliedJob?.eps_family_members || [],
      
      // EPS Nominee from JSON
      epsNominee: appliedJob?.eps_nominee || {},
    };
  }, [candidateData]);

  const handlePrint = useReactToPrint({
    contentRef: formRef,
    pageStyle: `
      @page {
        size: A4;
        margin: 10mm 12mm;
      }
      @media print {
        html, body {
          width: 210mm;
          min-height: 297mm;
          margin: 0;
          padding: 0;
        }
        .no-print {
          display: none !important;
        }
        .page-break {
          page-break-before: always;
        }
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `,
  });

  const cellStyle = {
    border: "1px solid #000",
    fontSize: "10.5px",
    p: 0.6,
    verticalAlign: "top",
  };

  const headerCellStyle = {
    ...cellStyle,
    fontWeight: "bold",
    textAlign: "center",
    backgroundColor: "#f5f5f5",
  };

  return (
    <Box>
      {/* Print Button */}
      <Box sx={{ textAlign: "center", mb: 2 }} className="no-print">
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
      </Box>

      <Box
        ref={formRef}
        sx={{
          backgroundColor: "#fff",
          fontFamily: '"Times New Roman", Times, serif',
          maxWidth: "210mm",
          mx: "auto",
          fontSize: "12px",
          lineHeight: 1.35,
          color: "#000",
        }}
      >
        {/* ===================== PAGE 1 ===================== */}
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{ position: "relative", mb: 1.5, minHeight: "50px" }}>
            <Box sx={{ position: "absolute", left: 0, top: 0 }}>
              <img
                src={
                  config?.IMAGE_PATH
                    ? config.IMAGE_PATH + "logo512.png"
                    : "/logo512.png"
                }
                alt="Logo"
                height="38"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/logo512.png";
                }}
              />
            </Box>
            <Typography
              align="center"
              sx={{ fontSize: "14px", fontWeight: "bold", pt: 0.2, pb: 2 }}
            >
              (FORM 2 REVISED)
            </Typography>
            <Typography
              align="center"
              sx={{ fontSize: "15px", fontWeight: "bold" }}
            >
              NOMINATION AND DECLARATION FORM FOR UNEXEMPTED/EXEMPTED
              ESTABLISHMENTS
            </Typography>
            <Typography
              align="center"
              sx={{ fontSize: "12.5px", mt: 0.3 }}
            >
              Declaration and Nomination Form under the Employees Provident Funds
              and Employees Pension Schemes
            </Typography>
            <Typography
              align="center"
              sx={{ fontSize: "12px" }}
            >
              (Paragraph 33 and 61 (1) of the Employees Provident Fund Scheme 1952
              and Paragraph 18 of the Employees Pension Scheme 1995)
            </Typography>
          </Box>

          {/* Personal Details */}
          <Typography sx={{ fontSize: "13.5px", mb: 0.6 }}>
            1. Name (IN BLOCK LETTERS) :{" "}
            <Box
              component="span"
              sx={{
                display: "inline-block",
                minWidth: "320px",
                borderBottom: "1px solid #000",
                px: 0.5,
              }}
            >
              {extractedData.fullName.toUpperCase()}
            </Box>
          </Typography>

          <Grid container spacing={1} sx={{ mb: 0.8 }}>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "12.5px", textAlign: "center" }}>
                Name
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "12.5px", textAlign: "center" }}>
                Father's / Husband's Name
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "12.5px", textAlign: "center" }}>
                Surname
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={4}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "13.5px",
                  gap: 0.5,
                }}
              >
                <Typography sx={{ fontSize: "13.5px", whiteSpace: "nowrap" }}>
                  2. Date of Birth :
                </Typography>
                <Box
                  component="span"
                  sx={{
                    borderBottom: "1px solid #000",
                    display: "inline-block",
                    minWidth: "140px",
                    px: 0.5,
                  }}
                >
                  {extractedData.dobFormatted || "_______"}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "13.5px" }}>
                3. Account No.{" "}
                <Box
                  component="span"
                  sx={{
                    borderBottom: "1px solid #000",
                    display: "inline-block",
                    minWidth: "120px",
                    px: 0.5,
                  }}
                >
                  {extractedData.accountNo || "________________"}
                </Box>
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "13.5px", whiteSpace: "nowrap" }}>
                4. *Sex ( MALE / FEMALE ) :{" "}
                <Box
                  component="span"
                  sx={{
                    borderBottom: "1px solid #000",
                    display: "inline-block",
                    minWidth: "66px",
                    px: 0.5,
                    verticalAlign: "bottom",
                  }}
                >
                  {extractedData.gender?.toUpperCase() || "____________"}
                </Box>
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography sx={{ fontSize: "13.5px" }}>
                5. Marital Status{" "}
                <Box
                  component="span"
                  sx={{
                    borderBottom: "1px solid #000",
                    display: "inline-block",
                    minWidth: "120px",
                    px: 0.5,
                  }}
                >
                  {extractedData.maritalStatus.toUpperCase()}
                </Box>
              </Typography>
            </Grid>
          </Grid>

          <Typography sx={{ fontSize: "13.5px", mb: 0.3 }}>
            6. Address Permanent / Temporary :
          </Typography>
          <Box
            sx={{
              borderBottom: "1px solid #000",
              minHeight: "20px",
              mb: 0.5,
              px: 0.5,
            }}
          >
            {extractedData.address}
          </Box>
          <Box
            sx={{
              borderBottom: "1px solid #000",
              minHeight: "20px",
              mb: 1.5,
            }}
          />

          {/* ============================================================ */}
          {/* ===================== TABLE 1: PART – A (EPF) ===================== */}
          {/* ============================================================ */}
          <Typography
            sx={{
              fontSize: "15.5px",
              fontWeight: "bold",
              textAlign: "center",
              mb: 0.8,
            }}
          >
            PART – A (EPF)
          </Typography>

          <Typography sx={{ fontSize: "13px", mb: 1, textAlign: "justify" }}>
            I hereby nominate the person(s)/cancel the nomination made by me
            previously and nominate the person(s) mentioned below to receive the
            amount standing to my credit in the Employees Provident Fund, in the
            event of my death.
          </Typography>

          {/* ============================================================ */}
          {/* TABLE 1: EPF Nominee Details Table - DYNAMIC DATA */}
          {/* Columns: 6 columns */}
          {/* Col 1: Name of the Nominee (s) - Width: 18% */}
          {/* Col 2: Address - Width: 18% */}
          {/* Col 3: Nominee's relationship with the member - Width: 14% */}
          {/* Col 4: Date of Birth - Width: 10% */}
          {/* Col 5: Total amount or share of accumulations in Provident Funds to be paid to each nominee - Width: 18% */}
          {/* Col 6: If the nominee is minor name and address of the guardian who may receive the amount during the minority of the nominee - Width: 22% */}
          {/* Data Source: extractedData.epfNominees */}
          {/* ============================================================ */}
          <TableContainer sx={{ border: "1px solid #000", mb: 1.2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellStyle, width: "18%" }}>
                    Name of the Nominee (s)
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "18%" }}>
                    Address
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "14%" }}>
                    Nominee's relationship with the member
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "10%" }}>
                    Date of Birth
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "18%" }}>
                    Total amount or share of accumulations in Provident Funds to be paid to each nominee
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "22%" }}>
                    If the nominee is minor name and address of the guardian who may receive the amount during the minority of the nominee
                  </TableCell>
                </TableRow>
                <TableRow>
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <TableCell
                      key={n}
                      sx={{ ...headerCellStyle, fontSize: "12px", p: 0.3 }}
                    >
                      {n}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {extractedData.epfNominees.length > 0 ? (
                  extractedData.epfNominees.map((nominee, index) => (
                    <TableRow key={nominee._id || index}>
                      <TableCell sx={{ ...cellStyle, height: "26px" }}>
                        {nominee.name || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "26px" }}>
                        {nominee.address || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "26px" }}>
                        {nominee.relationship || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "26px" }}>
                        {nominee.dob || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "26px" }}>
                        {nominee.share || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "26px" }}>
                        {nominee.guardian || ""}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  // Show 4 empty rows if no data
                  [1, 2, 3, 4].map((row) => (
                    <TableRow key={row}>
                      {Array.from({ length: 6 }).map((_, i) => (
                        <TableCell
                          key={i}
                          sx={{ ...cellStyle, height: "26px" }}
                        />
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* ============================================================ */}
          {/* ===================== END OF TABLE 1 ======================== */}
          {/* ============================================================ */}

          <Typography sx={{ fontSize: "13px", mb: 0.6 }}>
            1. *Certified that I have no family as defined in para 2 (g) of the
            Employees Provident Fund Scheme 1952 and should I acquire a family
            hereafter the above nomination should be deemed as cancelled.
          </Typography>
          <Typography sx={{ fontSize: "13px", mb: 1 }}>
            2. *Certified that my father/mother is/are dependent upon me.
          </Typography>

          <Grid container sx={{ mb: 1.5 }}>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: "12.5px" }}>
                Strike out whichever is not applicable
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography align="right" sx={{ fontSize: "13px" }}>
                Signature or thumb impression of the subscriber
              </Typography>
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  width: "200px",
                  height: "30px",
                  ml: "auto",
                  mt: 0.5,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {candidateData?.applicant_form_data?.signature && (
                  <img
                    src={`${config?.IMAGE_PATH || ""}${candidateData.applicant_form_data.signature}`}
                    alt="candidate-signature"
                    style={{
                      maxHeight: "22px",
                      maxWidth: "100%",
                      objectFit: "contain",
                    }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>

          {/* ============================================================ */}
          {/* ===================== TABLE 2: PART – (EPS) ===================== */}
          {/* ============================================================ */}
          <Typography
            sx={{
              fontSize: "15.5px",
              fontWeight: "bold",
              textAlign: "center",
              mb: 0.5,
            }}
          >
            PART – (EPS)
          </Typography>
          <Typography
            sx={{ fontSize: "13px", textAlign: "center", mb: 0.8 }}
          >
            Para 18
          </Typography>

          <Typography sx={{ fontSize: "13px", mb: 1, textAlign: "justify" }}>
            I hereby furnish below particulars of the members of my family who
            would be eligible to receive Widow/Children Pension in the event of
            my premature death in service.
          </Typography>

          {/* ============================================================ */}
          <TableContainer sx={{ border: "1px solid #000" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellStyle, width: "10%" }}>
                    Sr. No
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "45%" }}>
                    Name & Address of the Family Member
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "15%" }}>
                    Age
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "30%" }}>
                    Relationship with the member
                  </TableCell>
                </TableRow>
                <TableRow>
                  {["(1)", "(2)", "(3)", "(4)"].map((n) => (
                    <TableCell
                      key={n}
                      sx={{ ...headerCellStyle, fontSize: "12px", p: 0.3 }}
                    >
                      {n}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {extractedData.epsFamilyMembers.length > 0 ? (
                  extractedData.epsFamilyMembers.map((member, index) => (
                    <TableRow key={member._id || index}>
                      <TableCell
                        sx={{ ...cellStyle, height: "24px", textAlign: "center" }}
                      >
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "24px" }}>
                        {member.name || ""}
                        {member.address ? `, ${member.address}` : ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "24px" }}>
                        {member.age || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "24px" }}>
                        {member.relationship || ""}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  // Show 4 empty rows if no data
                  [1, 2, 3, 4].map((row) => (
                    <TableRow key={row}>
                      <TableCell
                        sx={{ ...cellStyle, height: "24px", textAlign: "center" }}
                      >
                        {row}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "24px" }} />
                      <TableCell sx={{ ...cellStyle, height: "24px" }} />
                      <TableCell sx={{ ...cellStyle, height: "24px" }} />
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* ============================================================ */}
          {/* ===================== END OF TABLE 2 ======================== */}
          {/* ============================================================ */}
        </Box>

        {/* ===================== PAGE 2 ===================== */}
        <Box className="page-break" sx={{ p: 2 }}>
          <Typography sx={{ fontSize: "13.5px", mb: 1.5, textAlign: "justify" }}>
            Certified that I have no family as defined in para 2 (vii) of the
            Employees's Family Pension Scheme 1995 and should I acquire a family
            hereafter I shall furnish Particulars there on in the above form.
          </Typography>

          <Typography sx={{ fontSize: "13.5px", mb: 1.2, textAlign: "justify" }}>
            I hereby nominate the following person for receiving the monthly
            widow pension (admissible under para 16 2 (a) (i) &amp; (ii) in the event
            of my death without leaving any eligible family member for receiving
            pension.
          </Typography>

          {/* ============================================================ */}
          <TableContainer sx={{ border: "1px solid #000", mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellStyle, width: "45%" }}>
                    Name and Address of the nominee
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "20%" }}>
                    Date of Birth
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "35%" }}>
                    Relationship with member
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {extractedData.epsNominee && extractedData.epsNominee.name ? (
                  <TableRow>
                    <TableCell sx={{ ...cellStyle, height: "70px" }}>
                      {extractedData.epsNominee.name || ""}
                      {extractedData.epsNominee.address 
                        ? `, ${extractedData.epsNominee.address}` 
                        : ""}
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, height: "70px" }}>
                      {extractedData.epsNominee.dob || ""}
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, height: "70px" }}>
                      {extractedData.epsNominee.relationship || ""}
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell sx={{ ...cellStyle, height: "70px" }} />
                    <TableCell sx={{ ...cellStyle, height: "70px" }} />
                    <TableCell sx={{ ...cellStyle, height: "70px" }} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* ============================================================ */}
          {/* ===================== END OF TABLE 3 ======================== */}
          {/* ============================================================ */}

          <Grid container sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: "13.5px" }}>
                Date{" "}
                <Box
                  component="span"
                  sx={{
                    borderBottom: "1px solid #000",
                    display: "inline-block",
                    minWidth: "150px",
                    px: 0.5,
                  }}
                >
                 {formatDate(doj)}
                </Box>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography align="right" sx={{ fontSize: "13.5px" }}>
                Signature or thumb impression of the subscriber
              </Typography>
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  width: "200px",
                  height: "30px",
                  ml: "auto",
                  mt: 0.5,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {candidateData?.applicant_form_data?.signature && (
                  <img
                    src={`${config?.IMAGE_PATH || ""}${candidateData.applicant_form_data.signature}`}
                    alt="candidate-signature"
                    style={{
                      maxHeight: "22px",
                      maxWidth: "100%",
                      objectFit: "contain",
                    }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>

          {/* CERTIFICATE BY EMPLOYER */}
          <Box
            sx={{
              borderTop: "2px solid #000",
              pt: 1.5,
              mt: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: "bold",
                textAlign: "center",
                mb: 1.5,
              }}
            >
              CERTIFICATE BY EMPLOYER
            </Typography>

            <Typography sx={{ fontSize: "13.5px", mb: 1.5, textAlign: "justify" }}>
              Certified that the above declaration and nomination has been signed
              / thumb impressed before me by Shri / Smt. / Miss{" "}
              <Box
                component="span"
                sx={{
                  borderBottom: "1px solid #000",
                  display: "inline-block",
                  minWidth: "250px",
                  px: 0.5,
                }}
              >
                {extractedData.fullName}
              </Box>{" "}
              employed in my establishment after he/she has read the entries /
              the entries have been read over to him/her by me and got confirmed
              by him/her.
            </Typography>

            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: "13.5px", mb: 1 }}>
                  Date :{" "}
                  <Box
                    component="span"
                    sx={{
                      borderBottom: "1px solid #000",
                      display: "inline-block",
                      minWidth: "120px",
                      px: 0.5,
                    }}
                  >
                    {formatDate(doj)}
                  </Box>
                </Typography>
                <Typography sx={{ fontSize: "13.5px", mb: 0.5 }}>
                  Name &amp; address of the Factory / Establishment
                </Typography>
                <Box
                  sx={{
                    border: "1px solid #000",
                    minHeight: "50px",
                    mb: 1,
                    px: 0.5,
                  }}
                >
                  {candidateData?.project_name || ""}
                  <br />
                  {extractedData?.annexureEleven?.place_of_posting || appliedJob?.proposed_location || appliedJob?.job_location || candidateData?.location || ""}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography align="right" sx={{ fontSize: "13.5px", mb: 0.5 }}>
                  Signature of the employer or other authorised officer of the
                  establishment
                </Typography>
                <Box
                  sx={{
                    borderBottom: "1px solid #000",
                    width: "220px",
                    height: "40px",
                    ml: "auto",
                    mb: 1.5,
                  }}
                />
                <Typography align="right" sx={{ fontSize: "13.5px", mb: 0.5 }}>
                  Place :{" "}
                  <Box
                    component="span"
                    sx={{
                      borderBottom: "1px solid #000",
                      display: "inline-block",
                      minWidth: "100px",
                      px: 0.5,
                    }}
                  >
                    { extractedData?.annexureEleven?.place_of_posting || appliedJob?.proposed_location || appliedJob?.job_location || candidateData?.location ||""}
                  </Box>
                </Typography>
                <Typography align="right" sx={{ fontSize: "13.5px" }}>
                  Date :{" "}
                  <Box
                    component="span"
                    sx={{
                      borderBottom: "1px solid #000",
                      display: "inline-block",
                      minWidth: "100px",
                      px: 0.5,
                    }}
                  >
                   {formatDate(doj)}
                  </Box>
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EPFForm2;